import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Trash2, Eye, Heart, Image, Video, Link2,
  Compass, Loader2, Check, X, Clock, ChevronDown, ChevronUp,
  ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Food & Beverage",
  "Travel",
  "Real Estate",
  "Health & Fitness",
  "Beauty & Skincare",
  "Tech & Gadgets",
  "Home & Lifestyle",
  "Others",
];

const REGIONS = ["Global", "US", "UK", "India"];

const PLAN_LABELS = { standard: "Discover Boost", prime: "Boost" };

// Inline scan status badge shown after file selection
function ScanBadge({ scan }) {
  if (scan.scanning) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
        <Loader2 size={12} className="animate-spin" /> Scanning file…
      </div>
    );
  }
  if (scan.safe) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400">
        <ShieldCheck size={12} /> Scan passed — {scan.reason} · {scan.size_kb} KB
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center gap-1.5 text-xs text-red-400 font-medium">
      <ShieldAlert size={12} /> {scan.reason}
    </div>
  );
}

const PALETTES = [
  { bg: "#fef3c7", text: "#78350f", sub: "#92400e" },
  { bg: "#dbeafe", text: "#1e3a5f", sub: "#1d4ed8" },
  { bg: "#dcfce7", text: "#14532d", sub: "#15803d" },
  { bg: "#fce7f3", text: "#831843", sub: "#be185d" },
  { bg: "#ede9fe", text: "#4c1d95", sub: "#6d28d9" },
  { bg: "#ffedd5", text: "#7c2d12", sub: "#c2410c" },
];

// ── Request card ──────────────────────────────────────────────
function RequestCard({ req, onApprove, onReject, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isPending = req.status === "pending";

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.post(`/admin/discover-requests/${req.id}/approve`);
      onApprove(req.id, req);
      toast.success("Request approved — form pre-filled below");
    } catch (err) {
      toast.error(formatApiError(err));
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await api.post(`/admin/discover-requests/${req.id}/reject`, { reason: "" });
      onReject(req.id);
      toast.success("Request rejected");
    } catch (err) {
      toast.error(formatApiError(err));
      setRejecting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete request from "${req.business_name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/discover-requests/${req.id}`);
      onDelete(req.id);
      toast.success("Request deleted");
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(false);
    }
  };

  const statusStyles = {
    pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    approved: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <div
      data-testid={`discover-request-${req.id}`}
      className={`border rounded-xl overflow-hidden transition-all ${
        isPending ? "border-amber-500/30 bg-amber-500/5" : "border-white/8 bg-white/3"
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-slate-100 truncate">{req.business_name}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusStyles[req.status] || ""}`}>
                {req.status}
              </span>
              <span className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">
                {PLAN_LABELS[req.plan] || req.plan}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
              <span>{req.contact_email}</span>
              <span>•</span>
              <span>{req.category}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isPending && (
              <>
                <button
                  data-testid={`discover-req-approve-${req.id}`}
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex items-center gap-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 text-xs font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                >
                  {approving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                  Approve
                </button>
                <button
                  data-testid={`discover-req-reject-${req.id}`}
                  onClick={handleReject}
                  disabled={rejecting}
                  className="flex items-center gap-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-xs font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                >
                  {rejecting ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
                  Reject
                </button>
              </>
            )}
            <button
              data-testid={`discover-req-delete-${req.id}`}
              onClick={handleDelete}
              disabled={deleting}
              title="Delete request"
              className="text-slate-600 hover:text-red-400 transition p-1 disabled:opacity-50"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            </button>
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-slate-600 hover:text-slate-400 transition p-1"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-white/8 space-y-2">
            {req.boost_type && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 uppercase tracking-wide">Boost type:</span>
                <span className="bg-violet-500/20 text-violet-300 border border-violet-500/30 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                  {req.boost_type}
                </span>
              </div>
            )}
            <p className="text-xs text-slate-400 leading-relaxed">{req.message}</p>
            {req.admin_note && (
              <p className="text-xs text-red-400 italic">Admin note: {req.admin_note}</p>
            )}
            <p className="text-[10px] text-slate-600">
              Submitted {new Date(req.created_at).toLocaleString()}
              {req.submitted_by ? ` by ${req.submitted_by}` : " (guest)"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Post card ─────────────────────────────────────────────────
function PostCard({ post, index, onDelete }) {
  const palette = PALETTES[index % PALETTES.length];
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${post.business_name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/discover/${post.id}`);
      onDelete(post.id);
      toast.success("Post deleted");
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid={`discover-admin-card-${post.id}`}
      style={{ backgroundColor: palette.bg }}
      className="rounded-2xl overflow-hidden shadow-md relative group"
    >
      <button
        data-testid={`discover-delete-btn-${post.id}`}
        onClick={handleDelete}
        disabled={deleting}
        className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-500 disabled:opacity-50"
      >
        {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
      </button>

      <div className="relative aspect-video overflow-hidden bg-black/10">
        <img src={post.thumbnail_url} alt={post.business_name} className="w-full h-full object-cover" loading="lazy" />
        {post.video_url && (
          <div className="absolute top-2 right-2 bg-black/60 rounded-full px-2 py-0.5 flex items-center gap-1">
            <Video size={11} className="text-white" />
            <span className="text-white text-[10px]">video</span>
          </div>
        )}
        {post.instagram_link && !post.video_url && (
          <div className="absolute top-2 right-2 bg-pink-500/80 rounded-full px-2 py-0.5 flex items-center gap-1">
            <Link2 size={11} className="text-white" />
            <span className="text-white text-[10px]">ig</span>
          </div>
        )}
        {post.category && (
          <div className="absolute bottom-2 left-2 bg-black/60 rounded-full px-2 py-0.5">
            <span className="text-white text-[10px]">{post.category}</span>
          </div>
        )}
      </div>

      <div className="p-3">
        <h3 className="font-bold text-sm truncate" style={{ color: palette.text }}>{post.business_name}</h3>
        <p className="text-xs mt-0.5 line-clamp-2 leading-relaxed" style={{ color: palette.sub, opacity: 0.8 }}>{post.tagline}</p>
        <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: palette.sub, opacity: 0.65 }}>
          <span className="flex items-center gap-1"><Eye size={11} />{post.view_count}</span>
          <span className="flex items-center gap-1"><Heart size={11} />{post.like_count}</span>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function DiscoverAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const thumbnailRef = useRef(null);
  const videoRef = useRef(null);
  const formRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [prefillSource, setPrefillSource] = useState(null); // request that pre-filled the form
  const [scanStatus, setScanStatus] = useState({}); // { thumbnail?: {safe,reason}, video?: {safe,reason} }

  const scanFile = async (file, key) => {
    setScanStatus((s) => ({ ...s, [key]: { scanning: true } }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/scan-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanStatus((s) => ({ ...s, [key]: { safe: data.safe, reason: data.reason, size_kb: data.size_kb } }));
      if (!data.safe) toast.error(`File blocked: ${data.reason}`);
    } catch {
      setScanStatus((s) => ({ ...s, [key]: { safe: null, reason: "Scan failed — proceed with caution" } }));
    }
  };

  const [form, setForm] = useState({
    business_name: "",
    tagline: "",
    category: CATEGORIES[0],
    region: "Global",
    instagram_link: "",
  });
  const [mediaMode, setMediaMode] = useState("instagram");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);

  useEffect(() => {
    if (user === null) return;
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/discover").then((r) => setPosts(r.data)).finally(() => setLoadingPosts(false));
    api.get("/admin/discover-requests").then((r) => setRequests(r.data)).finally(() => setLoadingReqs(false));
  }, [user]);

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    setThumbPreview(URL.createObjectURL(file));
    setScanStatus((s) => ({ ...s, thumbnail: null }));
    scanFile(file, "thumbnail");
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Video must be under 200 MB"); return; }
    setVideoFile(file);
    setScanStatus((s) => ({ ...s, video: null }));
    scanFile(file, "video");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    if (!form.tagline.trim()) { toast.error("Tagline is required"); return; }
    if (!thumbnailFile) { toast.error("Thumbnail image is required"); return; }
    if (scanStatus.thumbnail?.safe === false) { toast.error("Thumbnail blocked by safety scan — replace the file"); return; }
    if (scanStatus.video?.safe === false) { toast.error("Video blocked by safety scan — replace the file"); return; }
    if (["instagram", "twitter", "youtube", "post"].includes(mediaMode) && !form.instagram_link.trim()) {
      toast.error("Link URL is required"); return;
    }
    if (mediaMode === "video" && !videoFile) { toast.error("Please upload a video file"); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("business_name", form.business_name.trim());
      fd.append("tagline", form.tagline.trim());
      fd.append("category", form.category);
      fd.append("region", form.region);
      if (["instagram", "twitter", "youtube", "post"].includes(mediaMode) && form.instagram_link.trim()) {
        fd.append("instagram_link", form.instagram_link.trim());
      }
      fd.append("thumbnail", thumbnailFile);
      if (mediaMode === "video" && videoFile) fd.append("video", videoFile);

      const r = await api.post("/discover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPosts((prev) => [r.data, ...prev]);
      toast.success("Post added!");
      setForm({ business_name: "", tagline: "", category: CATEGORIES[0], region: "Global", instagram_link: "" });
      setThumbnailFile(null);
      setVideoFile(null);
      setThumbPreview(null);
      setPrefillSource(null);
      if (thumbnailRef.current) thumbnailRef.current.value = "";
      if (videoRef.current) videoRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <main data-testid="discover-admin-page" className="pt-28 md:pt-16 min-h-screen">
      <div className="px-4 sm:px-6 py-10 max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8 flex items-center gap-3">
          <Compass size={24} className="text-emerald-400" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-100">Discover Admin</h1>
            <p className="text-slate-500 text-sm mt-0.5">Manage posts and business feature requests.</p>
          </div>
        </div>

        {/* ── Business Requests ── */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-base font-semibold text-slate-200">
              Business Requests
            </h2>
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-black text-xs font-black px-2 py-0.5 rounded-full">
                {pendingCount} pending
              </span>
            )}
          </div>

          {loadingReqs && (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
              ))}
            </div>
          )}

          {!loadingReqs && requests.length === 0 && (
            <div data-testid="discover-requests-empty" className="text-center py-10 text-slate-600 border border-white/8 rounded-xl">
              <Clock size={28} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No feature requests yet.</p>
            </div>
          )}

          {!loadingReqs && requests.length > 0 && (
            <div className="space-y-2">
              {requests.map((req) => (
                <RequestCard
                  key={req.id}
                  req={req}
                  onApprove={(id, reqData) => {
                    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "approved" } : r));
                    // Pre-fill the Add New Post form with request details
                    setForm({
                      business_name: reqData.business_name,
                      tagline: (reqData.message || "").substring(0, 140),
                      category: reqData.category || CATEGORIES[0],
                      region: "Global",
                      instagram_link: "",
                    });
                    setPrefillSource(reqData);
                    // Scroll to form
                    setTimeout(() => {
                      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 300);
                  }}
                  onReject={(id) =>
                    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "rejected" } : r))
                  }
                  onDelete={(id) =>
                    setRequests((prev) => prev.filter((r) => r.id !== id))
                  }
                />
              ))}
            </div>
          )}
        </section>

        {/* ── Add Post Form ── */}
        <section ref={formRef} className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-10">
          <h2 className="text-base font-semibold text-slate-200 mb-4 flex items-center gap-2">
            <Plus size={16} className="text-emerald-400" /> Add New Post
          </h2>

          {/* Pre-fill banner */}
          {prefillSource && (
            <div className="flex items-center justify-between gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2.5 mb-4">
              <div className="flex items-center gap-2 text-xs text-emerald-300">
                <Check size={13} className="shrink-0" />
                <span>Pre-filled from <span className="font-semibold">{prefillSource.business_name}</span>'s request — add a thumbnail to publish.</span>
              </div>
              <button
                type="button"
                onClick={() => { setPrefillSource(null); setForm({ business_name: "", tagline: "", category: CATEGORIES[0], region: "Global", instagram_link: "" }); }}
                className="text-emerald-500 hover:text-emerald-300 transition-colors shrink-0"
              >
                <X size={13} />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Business Name *</label>
                <input
                  data-testid="discover-form-name"
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
                  placeholder="e.g. CrawlWise"
                  maxLength={80}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Tagline *</label>
                <input
                  data-testid="discover-form-tagline"
                  type="text"
                  value={form.tagline}
                  onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
                  placeholder="One-line description"
                  maxLength={140}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Category *</label>
              <select
                data-testid="discover-form-category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-emerald-500/50 transition"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Region</label>
              <select
                data-testid="discover-form-region"
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-emerald-500/50 transition"
              >
                {REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">
                <span className="flex items-center gap-1.5"><Image size={13} /> Thumbnail Image *</span>
              </label>
              <div className="flex items-start gap-4">
                <label className="flex-1 flex items-center justify-center gap-2 bg-white/5 border border-dashed border-white/20 rounded-xl px-4 py-3 cursor-pointer hover:border-emerald-500/40 transition text-sm text-slate-400">
                  <Image size={15} />
                  {thumbnailFile ? thumbnailFile.name : "Click to choose image (png/jpg/webp)"}
                  <input
                    ref={thumbnailRef}
                    data-testid="discover-form-thumbnail"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    className="hidden"
                    onChange={handleThumbnailChange}
                  />
                </label>
                {thumbPreview && (
                  <img src={thumbPreview} alt="preview" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                )}
              </div>
              {/* Scan badge */}
              {scanStatus.thumbnail && (
                <ScanBadge scan={scanStatus.thumbnail} />
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-2">Link / Media Type *</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { mode: "instagram", icon: <Link2 size={13} />, label: "Instagram", activeClass: "bg-pink-500/20 border-pink-500/40 text-pink-300" },
                  { mode: "twitter",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.736-8.849L1.254 2.25H8.08l4.253 5.622 5.912-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>, label: "X / Twitter", activeClass: "bg-slate-500/20 border-slate-400/40 text-slate-200" },
                  { mode: "youtube",   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>, label: "YouTube", activeClass: "bg-red-500/20 border-red-500/40 text-red-300" },
                  { mode: "post",      icon: <Link2 size={13} />, label: "Post / Content", activeClass: "bg-amber-500/20 border-amber-500/40 text-amber-300" },
                  { mode: "video",     icon: <Video size={13} />, label: "Upload Video", activeClass: "bg-blue-500/20 border-blue-500/40 text-blue-300" },
                ].map(({ mode, icon, label, activeClass }) => (
                  <button
                    key={mode}
                    type="button"
                    data-testid={`discover-mode-${mode}`}
                    onClick={() => setMediaMode(mode)}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition ${
                      mediaMode === mode ? activeClass : "bg-white/5 border-white/10 text-slate-400 hover:border-white/20"
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            </div>

            {(mediaMode === "instagram" || mediaMode === "twitter" || mediaMode === "youtube" || mediaMode === "post") && (
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">
                  {mediaMode === "instagram" && "Instagram URL *"}
                  {mediaMode === "twitter" && "X / Twitter URL *"}
                  {mediaMode === "youtube" && "YouTube URL *"}
                  {mediaMode === "post" && "Post / Content URL *"}
                </label>
                <input
                  data-testid="discover-form-instagram"
                  type="url"
                  value={form.instagram_link}
                  onChange={(e) => setForm((f) => ({ ...f, instagram_link: e.target.value }))}
                  placeholder={
                    mediaMode === "instagram" ? "https://www.instagram.com/example/" :
                    mediaMode === "twitter" ? "https://x.com/example" :
                    mediaMode === "youtube" ? "https://www.youtube.com/@channel or https://youtu.be/videoID" :
                    "https://yourpost.com/link"
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition"
                />
              </div>
            )}

            {mediaMode === "video" && (
              <>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">
                  <span className="flex items-center gap-1.5"><Video size={13} /> Video File * (mp4/mov/webm, max 200 MB)</span>
                </label>
                <label className="flex items-center justify-center gap-2 bg-white/5 border border-dashed border-white/20 rounded-xl px-4 py-3 cursor-pointer hover:border-blue-500/40 transition text-sm text-slate-400">
                  <Video size={15} />
                  {videoFile ? videoFile.name : "Click to choose video"}
                  <input
                    ref={videoRef}
                    data-testid="discover-form-video"
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                    className="hidden"
                    onChange={handleVideoChange}
                  />
                </label>
              </div>
              {scanStatus.video && <ScanBadge scan={scanStatus.video} />}
              </>
            )}

            <button
              data-testid="discover-form-submit"
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-bold text-sm px-6 py-2.5 rounded-xl transition-all"
            >
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
              {submitting ? "Uploading..." : "Add Post"}
            </button>
          </form>
        </section>

        {/* ── Posts grid ── */}
        <section>
          <h2 className="text-base font-semibold text-slate-200 mb-5">
            All Posts <span className="text-slate-500 font-normal">({posts.length})</span>
          </h2>

          {loadingPosts && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-white/5 animate-pulse" style={{ aspectRatio: "3/4" }} />
              ))}
            </div>
          )}

          {!loadingPosts && posts.length === 0 && (
            <div data-testid="discover-admin-empty" className="text-center py-12 text-slate-600 border border-white/8 rounded-xl">
              <Compass size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No posts yet. Add one above.</p>
            </div>
          )}

          {!loadingPosts && posts.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {posts.map((post, i) => (
                <PostCard
                  key={post.id}
                  post={post}
                  index={i}
                  onDelete={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
